import { useNavigate } from "react-router-dom";
import AddTestKit from "../test-detail/add-testkit";
import {
  ColumnType,
  ExpectedRowTypes,
  TestKitsListType,
  TestKitType,
  UserType,
} from "@/common/types";
import { useEffect, useState } from "react";
import Table from "@/components/table";
import { convertDateToString } from "@/utils/date.util";
import { useDispatch, useSelector } from "react-redux";
import { downloadResult, getTestsKits } from "@/redux/actions/testKitActions";
import { RootState } from "@/redux/reducers";
import {
  isUserAdmin,
  isUserDoctor,
  isUserPatient,
  transformKitStatus,
} from "@/utils/common.util";
import AutoComplete from "@/components/autocomplete";
import { getAllDoctors, getAllPatients } from "@/redux/actions/usersActions";
import useDebounce from "@/hooks/use-debounce";
import CustomSelect from "@/components/select";
import { TestKitStatusOptions } from "@/common/constant";
import { Eye, RotateCcw } from "lucide-react";
import CardsForTable from "@/components/cards-for-table";

const columns: ColumnType[] = [
  { field: "kit_id", headerName: "Kit ID" },
  {
    field: "assigned_doctor",
    headerName: "Doctor",
    render: (row) => (row as TestKitType).assigned_doctor?.name || "-",
  },
  {
    field: "assigned_patient",
    headerName: "Patient",
    render: (row) => (row as TestKitType).assigned_patient?.name || "-",
  },
  {
    field: "status",
    headerName: "Status",
    render: (row) => transformKitStatus((row as TestKitType).status),
  },
  {
    field: "created_at",
    headerName: "Created At",
    render: (row) =>
      convertDateToString((row as TestKitType).created_at) || "-",
  },
  {
    field: "updated_at",
    headerName: "Updated At",
    render: (row) =>
      convertDateToString((row as TestKitType).updated_at) || "-",
  },
];

const initialFilterState = {
  doctor: "",
  patient: "",
  status: "",
};

const Tests = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState<string>("");
  const debouncedDoctorSearchTerm = useDebounce(doctorSearchTerm, 500);
  const [patientSearchTerm, setPatientSearchTerm] = useState<string>("");
  const debouncedPatientSearchTerm = useDebounce(patientSearchTerm, 500);

  const [filterState, setFilterState] = useState(initialFilterState);

  const rowsPerPage = 10;

  const testKits = useSelector((state: RootState) => state.testKit).testKits;
  const { doctors, patients } = useSelector((state: RootState) => state.users);

  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: any = useDispatch();

  const getColumns = () => {
    const filteredColumns: ColumnType[] = [];

    if (isUserAdmin()) {
      filteredColumns.push(...columns);
    } else if (isUserDoctor()) {
      ["assigned_patient", "kit_id", "status"].forEach((column) => {
        const columnToPush = columns.find((col) => col.field === column);
        if (columnToPush !== undefined) {
          filteredColumns.push(columnToPush);
        }
      });
    } else if (isUserPatient()) {
      ["kit_id", "assigned_doctor", "status"].forEach((column) => {
        const columnToPush = columns.find((col) => col.field === column);
        if (columnToPush !== undefined) {
          filteredColumns.push(columnToPush);
        }
      });

      const viewPdfColumn: ColumnType = {
        field: "",
        headerName: "Report",
        render: (row) =>
          (row as TestKitType).status === "REPORT_READY" ? (
            <div
              className="flex gap-1 items-center cursor-pointer"
              onClick={(e) => handleViewPdf(e, row as TestKitType)}
            >
              <Eye className="h-5 w-5" />
              View/Download
            </div>
          ) : (
            <div>-</div>
          ),
      };
      filteredColumns.push(viewPdfColumn);
    }
    return filteredColumns;
  };

  const handleDownloadResult = async (row: TestKitType) => {
    dispatch(downloadResult(Number(row.id)));
  };

  const handleViewPdf = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    row: TestKitType
  ) => {
    e.stopPropagation();
    e.preventDefault();
    await handleDownloadResult(row);
  };

  const handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: ExpectedRowTypes
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if ("id" in row) {
      navigate(`/test/${row.id}`);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelect = (key: string, value: string) => {
    setFilterState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  const resetCurrentPage = () => {
    setCurrentPage(1);
  };

  const resetFilter = () => {
    setFilterState(initialFilterState);
    resetCurrentPage();
  };

  useEffect(() => {
    dispatch(getTestsKits(currentPage, filterState));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterState]);

  useEffect(() => {
    dispatch(getAllDoctors(debouncedDoctorSearchTerm));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDoctorSearchTerm]);

  useEffect(() => {
    dispatch(getAllPatients(debouncedPatientSearchTerm));
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPatientSearchTerm]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between mb-4">
        {!isUserPatient() && (
          <div className="flex gap-2 flex-wrap">
            {!isUserDoctor() && (
              <div className="w-[48%] md:w-[175px]">
                <AutoComplete
                  selectedValue={filterState.doctor}
                  placeHolder="doctor"
                  options={doctors as UserType[]}
                  labelKey="name"
                  onSelect={(option) => handleSelect("doctor", option?.id)}
                  onChange={(search) => setDoctorSearchTerm(search)}
                  listCount={5}
                />
              </div>
            )}
            <div className="w-[48%] md:w-[175px]">
              <AutoComplete
                selectedValue={filterState.patient}
                placeHolder="patient"
                options={patients as UserType[]}
                labelKey="name"
                onSelect={(option) => handleSelect("patient", option?.id)}
                onChange={(search) => setPatientSearchTerm(search)}
                listCount={5}
              />
            </div>
            <div className="w-[48%] md:w-[175px]">
              <CustomSelect
                value={filterState.status}
                options={TestKitStatusOptions}
                onChange={(option) => handleSelect("status", option?.value)}
                placeholder="Status"
                labelKey="label"
                valueKey="value"
              />
            </div>
            <RotateCcw
              className="cursor-pointer w-[40px] h-[40px] border border-black px-[10px] rounded-md"
              onClick={resetFilter}
            />
          </div>
        )}
        <div className="hidden md:block">{isUserAdmin() && <AddTestKit />}</div>
      </div>

      <div className="flex md:hidden mb-4 justify-end">
        {isUserAdmin() && <AddTestKit />}
      </div>

      <div>
        <div className="hidden md:flex items-center justify-center">
          <Table
            rows={(testKits as TestKitsListType)?.results || []}
            columns={getColumns()}
            rowsPerPage={rowsPerPage}
            selectedPage={currentPage}
            totalPages={
              Math.ceil((testKits as TestKitsListType)?.count / rowsPerPage) ||
              0
            }
            showPrevious={(testKits as TestKitsListType)?.previous !== null}
            showNext={(testKits as TestKitsListType)?.next !== null}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
            onPageChange={handlePageChange}
          />
        </div>

        <div className="flex md:hidden">
          <CardsForTable
            rows={(testKits as TestKitsListType)?.results || []}
            columns={getColumns()}
            rowsPerPage={rowsPerPage}
            selectedPage={currentPage}
            totalPages={
              Math.ceil((testKits as TestKitsListType)?.count / rowsPerPage) ||
              0
            }
            showPrevious={(testKits as TestKitsListType)?.previous !== null}
            showNext={(testKits as TestKitsListType)?.next !== null}
            onRowClick={(e, row) =>
              handleRowClick(
                e as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
                row
              )
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Tests;
